"use client";

import { useState, useEffect } from "react";
import UnlockPreview from "./UnlockPreview";
import { useT } from "@/hooks/useT";

const FAQ_KEYS = [
  { qKey: "mobileShowcase.faq1.q", aKey: "mobileShowcase.faq1.a" },
  { qKey: "mobileShowcase.faq2.q", aKey: "mobileShowcase.faq2.a" },
  { qKey: "mobileShowcase.faq3.q", aKey: "mobileShowcase.faq3.a" },
] as const;

export default function MobileManagementSection() {
  const t = useT();
  const faqs = FAQ_KEYS.map(f => ({ question: t(f.qKey), answer: t(f.aKey) }));
  const [openIndex, setOpenIndex] = useState(-1);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = () => setIsMobile(window.innerWidth < 1024);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return (
    <section
      id="telegram-bot"
      aria-labelledby="telegram-bot-heading"
      className="bg-gray-50 dark:bg-[#0f0f12] py-12 sm:py-24 px-6 sm:px-12 lg:px-24 relative z-0 overflow-x-hidden"
    >
      <div className="relative z-10 flex flex-col items-center gap-12 mx-auto max-w-7xl lg:flex-row">
        {/* Left visual - Telegram Bot Preview */}
        <div
          className="relative z-10 flex justify-center w-full max-w-full overflow-hidden lg:w-1/2"
          aria-label="FrenFlow Telegram copy trading bot interface preview"
        >
          <div className="relative z-10 w-[288px] lg:w-[533px]">
            <UnlockPreview />
          </div>
        </div>

        {/* Right content */}
        <div className="w-full lg:w-1/2">
          <p className="text-sm font-inter-tight font-semibold text-accent-primary uppercase mb-2 tracking-wide">
            {t("mobileShowcase.eyebrow")}
          </p>
          <h2
            id="telegram-bot-heading"
            className="text-4xl font-monument font-extrabold text-gray-900 dark:text-white mb-4"
          >
            {t("mobileShowcase.headline.before")}{" "}
            <span className="text-accent-primary">{t("mobileShowcase.headline.accent")}</span>{" "}
            {t("mobileShowcase.headline.after")}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 font-satoshi mb-4 max-w-xl">
            {t("mobileShowcase.subtitle1")}
          </p>
          <p className="text-sm text-gray-400 dark:text-gray-500 font-satoshi mb-8 max-w-xl">
            {t("mobileShowcase.subtitle2")}
          </p>

          {/* Accordion */}
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px]"
              >
                <button
                  className="w-full text-left px-6 py-4 flex justify-between items-center font-monument font-semibold text-gray-900 dark:text-white"
                  onClick={() => setOpenIndex(openIndex === i ? -1 : i)}
                >
                  {faq.question}
                  <span className="text-2xl text-gray-400">
                    {openIndex === i ? "−" : "+"}
                  </span>
                </button>
                {openIndex === i && (
                  <div className="px-6 pb-4 font-satoshi text-gray-600 dark:text-gray-400">{faq.answer}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
